import View from '@play-co/timestep-core/lib/ui/View';
import ImageView from '@play-co/timestep-core/lib/ui/ImageView';
import QuickViewPool from '@play-co/timestep-core/lib/ui/ViewPool2';
import { getRandomFloat, getRandomInt } from 'src/replicant/utils/random';

class ExplosionSprite extends ImageView {
  public f!: number;
  public fmax!: number;
  public vx!: number;
  public vy!: number;
  public delay!: number;
}

let existingExplosion: Explosion;

type Opts = {
  // View opts.
  superview: View;
  zIndex?: number;

  // Explosion opts.
  sc: number;
  image: string;
  max: number;
  startX: number;
  startY: number;

  spreadMode?: 'burst' | 'fountain' | 'mix';
};

type Coords = {
  x: number;
  y: number;
};

class Explosion extends View {
  private sc!: number;
  private imageUrl!: string;
  private gravity: number;
  private sprites: ExplosionSprite[];
  private pool: QuickViewPool<ExplosionSprite>;
  private delayOffset: number;

  constructor(opts: Opts) {
    super({
      ...opts,
      canHandleEvents: false,
      width: 0,
      height: 0,
      zIndex: 100000,
    });

    this.gravity = 5;
    this.sprites = [];
    this.delayOffset = 0;

    this.pool = new QuickViewPool(
      () =>
        new ExplosionSprite({
          superview: this,
          centerOnOrigin: true,
          centerAnchor: true,
        }),
    );

    this.start(opts);
  }

  public start(opts: Opts) {
    this.sc = opts.sc;
    this.imageUrl = opts.image;
    this.delayOffset = 0;

    // create explosion sprite particles
    for (let i = 0; i < opts.max; i++) {
      this.sprites.push(this.createSprite(opts));
    }

    this.style.visible = true;
  }

  public createSprite({ startX, startY, spreadMode }: Opts) {
    // todo: pass this from outside
    const size = getRandomInt(32, 76, Math.random);

    // using timestep's ViewPool2
    // sprite pooling system to increase performance
    const sprite = this.pool.obtainView();

    // update timestep view props
    sprite.updateOpts({
      image: this.imageUrl,
      scale: this.sc,
      x: startX,
      y: startY,
      width: size,
      height: size,
      offsetX: -size / 2,
      offsetY: -size / 2,
      visible: false,
    });

    // update custom props
    sprite.vx = getRandomInt(-15, 15, Math.random) * 1.5;
    sprite.vy = getRandomInt(-42, 15, Math.random) * 1.5;
    sprite.f = 0;
    sprite.fmax = 32; // life duration of each particle in frames

    // set it to 2 for fountain style. Need to implement better...
    // sprite.delay = getRandomInt(-1, 2);

    let delay = getRandomInt(-1, 2, Math.random);
    if (spreadMode === 'burst') {
      delay = 0;
    } else if (spreadMode === 'fountain') {
      delay = getRandomFloat(0, 1.5, Math.random);
    }

    this.delayOffset += delay / 2;
    sprite.delay = this.delayOffset;

    return sprite;
  }

  public tick(_: number) {
    // uopdate particles
    for (let i = this.sprites.length - 1; i >= 0; i--) {
      const sprite = this.sprites[i];
      const me = sprite.style;

      sprite.f += 1;
      me.visible = sprite.f >= sprite.delay;

      if (!me.visible) {
        continue;
      }

      // add gravity to velocity on y axis
      sprite.vy += this.gravity;

      // update position
      me.x += sprite.vx;
      me.y += sprite.vy;

      // kill particle when her life ends
      if (sprite.f >= sprite.fmax + sprite.delay) {
        this.pool.releaseView(sprite);
        this.sprites.splice(i, 1);
      }
    }

    // Explosion finished
    if (this.sprites.length === 0 && this.style.visible) {
      this.style.visible = false;
    }
  }
}

export default function playExplosion(opts: Opts): void {
  if (existingExplosion) {
    existingExplosion.start(opts);
  } else {
    existingExplosion = new Explosion(opts);
  }
}
